import React, { useEffect, useMemo, useState } from "react";
import { Button, Flex, Input, LabelWrapper, Select, Table, Text } from "@theorgchartdev/storybook";

import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { appstr } from "@coreapp/utils/appstr";
import { FetchAuditMeta, FetchAuditReport, FetchAuditSnapshot } from "@/Utils/Promises";
import { GenerateAuditSnapshotDownload } from "./snapshotDownload";
import { GenerateCsvDownload } from "./csvDownload";
import { GenerateXlsxDownload } from "./xlsxDownload";

import {
  AuditEventType,
  AuditOutcome,
  AuditReportMode,
  AUDIT_REPORT_PAGE_SIZE_DEFAULT
} from "./constants";
import { getEventTypeLabel, getOutcomeLabel, getSourceLabel } from "./labels";

// One filter value per dropdown. Sent to the backend as a 1-element list
// to match the existing `$in` shape on event_type / source / outcome.
const initialFilters = {
  startDate: "",
  endDate: "",
  eventType: "",
  source: "",
  outcome: "",
  actorId: "",
  targetId: ""
};

const formatTimestamp = ts => {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch (e) {
    return ts;
  }
};

export const AuditLog = () => {
  // Two-tier filter state: `filters` is the in-progress draft bound to the
  // form inputs, `appliedFilters` is what the loaded records / pagination /
  // exports actually use. They sync only on Apply or Reset, so typing into a
  // filter input does not silently change what the next Next-page click or
  // CSV export produces.
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [meta, setMeta] = useState({ eventTypes: [], sources: [], outcomes: [] });
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(AUDIT_REPORT_PAGE_SIZE_DEFAULT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    FetchAuditMeta()
      .then(payload => {
        if (!payload) return;
        setMeta({
          eventTypes: payload.eventTypes || Object.values(AuditEventType),
          sources: payload.sources || [],
          outcomes: payload.outcomes || Object.values(AuditOutcome)
        });
      })
      .catch(() => {
        setMeta({
          eventTypes: Object.values(AuditEventType),
          sources: [],
          outcomes: Object.values(AuditOutcome)
        });
      });
  }, []);

  const loadRecords = async (nextPage = 0, filtersOverride = null) => {
    setLoading(true);
    setError(null);
    const effectiveFilters = filtersOverride || appliedFilters;
    try {
      const payload = await FetchAuditReport({
        ...filtersToRequest(effectiveFilters),
        mode: AuditReportMode.LIST,
        page: nextPage,
        pageSize
      });
      setRecords(payload.records || []);
      setTotal(payload.total || 0);
      setPage(payload.page || 0);
    } catch (err) {
      setError(appstr("auditLog.error.loadFailed"));
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(0);
  }, []);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    loadRecords(0, filters);
  };

  const handleResetFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    loadRecords(0, initialFilters);
  };

  const handleExportCsv = async () => {
    try {
      const payload = await FetchAuditReport({
        ...filtersToRequest(appliedFilters),
        mode: AuditReportMode.EXPORT_CSV
      });
      if (payload && payload.csv) {
        GenerateCsvDownload(payload.csv, "audit-log.csv");
      }
    } catch (err) {
      setError(appstr("auditLog.error.exportFailed"));
    }
  };

  const handleExportXlsx = async () => {
    try {
      const payload = await FetchAuditReport({
        ...filtersToRequest(appliedFilters),
        mode: AuditReportMode.EXPORT_XLSX
      });
      if (payload && payload.xlsxBase64) {
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        GenerateXlsxDownload(payload.xlsxBase64, `audit-report-${stamp}.xlsx`);
      }
    } catch (err) {
      setError(appstr("auditLog.error.exportFailed"));
    }
  };

  const handleGenerateSnapshot = async () => {
    try {
      const payload = await FetchAuditSnapshot();
      if (payload && payload.snapshot) {
        GenerateAuditSnapshotDownload(payload.snapshot);
      }
    } catch (err) {
      setError(appstr("auditLog.error.snapshotFailed"));
    }
  };

  // Round-5 (2026-06-12): storybook <Select> does NOT have an onChange or
  // onSelect prop — the selection callback is attached PER-OPTION via
  // `clickHandler` (see ListItem.d.ts and BranchLevelSecurityModal.js for
  // canonical usage). Earlier attempts at top-level `onChange` (round 3)
  // and `onSelect` (round 4) were silently ignored, which is why the
  // dropdowns appeared inert. We use functional setState so each handler
  // sees the latest filter state without needing `filters` in the dep
  // array (which would recreate the options on every keystroke).
  const eventTypeOptions = useMemo(
    () => [
      {
        label: appstr("All"),
        value: "",
        clickHandler: () => setFilters(prev => ({ ...prev, eventType: "" })),
        dataTestId: "account-settings-admin-log-event-type-all-option"
      },
      ...meta.eventTypes.map(e => ({
        label: getEventTypeLabel(e),
        value: e,
        clickHandler: () => setFilters(prev => ({ ...prev, eventType: e })),
        dataTestId: `account-settings-admin-log-event-type-${e}-option`
      }))
    ],
    [meta.eventTypes]
  );
  const outcomeOptions = useMemo(
    () => [
      {
        label: appstr("All"),
        value: "",
        clickHandler: () => setFilters(prev => ({ ...prev, outcome: "" })),
        dataTestId: "account-settings-admin-log-outcome-all-option"
      },
      ...meta.outcomes.map(o => ({
        label: getOutcomeLabel(o),
        value: o,
        clickHandler: () => setFilters(prev => ({ ...prev, outcome: o })),
        dataTestId: `account-settings-admin-log-outcome-${o}-option`
      }))
    ],
    [meta.outcomes]
  );
  const sourceOptions = useMemo(
    () => [
      {
        label: appstr("All"),
        value: "",
        clickHandler: () => setFilters(prev => ({ ...prev, source: "" })),
        dataTestId: "account-settings-admin-log-source-all-option"
      },
      ...meta.sources.map(s => ({
        label: getSourceLabel(s),
        value: s,
        clickHandler: () => setFilters(prev => ({ ...prev, source: s })),
        dataTestId: `account-settings-admin-log-source-${s}-option`
      }))
    ],
    [meta.sources]
  );

  const tableData = useMemo(
    () =>
      records.map((r, idx) => ({
        id: `${r.correlation_id || ""}-${idx}`,
        timestamp: formatTimestamp(r.ts),
        eventType: getEventTypeLabel(r.event_type),
        outcome: getOutcomeLabel(r.outcome),
        source: getSourceLabel(r.source),
        actor: (r.actor && (r.actor.email || r.actor.id)) || "",
        target: (r.target && (r.target.email || r.target.id)) || "",
        diffKeys: (r.diff_keys || []).join(", "),
        message: r.message || ""
      })),
    [records]
  );

  return (
    <SectionCardGroup>
      <SectionCard title={appstr("auditLog.title")}>
        <Flex direction="column" gap="16px">
          <Flex gap="8px" wrap="wrap">
            {/*
              Storybook <Input> only declares text/textarea/number/password/
              search/email/tel/url types — no "date". A native <input
              type="date"> is wrapped in LabelWrapper for consistent labelling
              with the rest of the filter row.
            */}
            <LabelWrapper label={appstr("auditLog.filter.startDate")} compact>
              <input
                type="date"
                data-testid="account-settings-admin-log-start-date-input"
                value={filters.startDate}
                onChange={e => setFilters({ ...filters, startDate: e.target.value })}
              />
            </LabelWrapper>
            <LabelWrapper label={appstr("auditLog.filter.endDate")} compact>
              <input
                type="date"
                data-testid="account-settings-admin-log-end-date-input"
                value={filters.endDate}
                onChange={e => setFilters({ ...filters, endDate: e.target.value })}
              />
            </LabelWrapper>
            {/*
              Round-3 review (2026-05): the per-Select `label` prop did not
              render visually in the storybook build, so the three "All"
              dropdowns appeared unlabelled. Wrap each Select in a
              <LabelWrapper> (same pattern we use for the native date
              inputs) so the header is always shown.
            */}
            {/*
              Selection is wired via each option's `clickHandler` (see the
              options arrays above). Top-level onChange/onSelect props on
              <Select> do not exist in this storybook build — earlier
              attempts to set them were silently ignored. ListItemOption is
              the canonical interface; see ListItem.d.ts.
            */}
            <LabelWrapper label={appstr("auditLog.filter.eventType")} compact>
              <Select
                dataTestId="account-settings-admin-log-event-type-select"
                selectSize="medium"
                listWidth="medium"
                options={eventTypeOptions}
                value={filters.eventType}
              />
            </LabelWrapper>
            <LabelWrapper label={appstr("auditLog.filter.source")} compact>
              <Select
                dataTestId="account-settings-admin-log-source-select"
                selectSize="medium"
                listWidth="medium"
                options={sourceOptions}
                value={filters.source}
              />
            </LabelWrapper>
            <LabelWrapper label={appstr("auditLog.filter.outcome")} compact>
              <Select
                dataTestId="account-settings-admin-log-outcome-select"
                selectSize="medium"
                listWidth="small"
                options={outcomeOptions}
                value={filters.outcome}
              />
            </LabelWrapper>
            <Input
              dataTestId="account-settings-admin-log-actor-id-input"
              label={appstr("auditLog.filter.actorId")}
              compact
              size="medium"
              value={filters.actorId}
              onChange={(_e, value) => setFilters({ ...filters, actorId: value })}
            />
            <Input
              dataTestId="account-settings-admin-log-target-id-input"
              label={appstr("auditLog.filter.targetId")}
              compact
              size="medium"
              value={filters.targetId}
              onChange={(_e, value) => setFilters({ ...filters, targetId: value })}
            />
          </Flex>
          <Flex gap="8px">
            <Button
              modifier="primary"
              onClick={handleApplyFilters}
              disabled={loading}
              dataTestId="account-settings-admin-log-apply-filters-btn"
            >
              {appstr("auditLog.action.apply")}
            </Button>
            <Button
              modifier="secondary"
              onClick={handleResetFilters}
              disabled={loading}
              dataTestId="account-settings-admin-log-reset-filters-btn"
            >
              {appstr("auditLog.action.reset")}
            </Button>
            <Button
              modifier="primary"
              onClick={handleExportXlsx}
              disabled={loading}
              dataTestId="account-settings-admin-log-export-xlsx-btn"
            >
              {appstr("auditLog.action.exportXlsx")}
            </Button>
            <Button
              modifier="secondary"
              onClick={handleExportCsv}
              disabled={loading}
              dataTestId="account-settings-admin-log-export-csv-btn"
            >
              {appstr("auditLog.action.exportCsv")}
            </Button>
            <Button
              modifier="secondary"
              onClick={handleGenerateSnapshot}
              disabled={loading}
              dataTestId="account-settings-admin-log-generate-snapshot-btn"
            >
              {appstr("auditLog.action.generateSnapshot")}
            </Button>
          </Flex>
          {error && (
            <Text colorModifier="danger" size="medium">
              {error}
            </Text>
          )}
          <Text size="small" colorModifier="grey">
            {appstr("auditLog.totalRecords")}: {total}
          </Text>
          {/*
            Sort disabled intentionally: storybook's Table sort comparator
            dereferences sortConfig.order without a null-guard, which crashes
            when the table has rows but no column has been clicked for
            sorting yet. Records arrive pre-sorted by timestamp DESC from the
            server, which is the natural audit-log order anyway.
          */}
          <Table
            tableLayout="fixed"
            columns={buildAuditColumns()}
            data={tableData}
            showHeader={true}
            sortEnabled={false}
            rowSize="largeR"
            textSize="mediumT"
            filterEnabled={false}
          />
          <Flex gap="8px" justifyContent="flex-end">
            <Button
              modifier="secondary"
              disabled={page === 0 || loading}
              onClick={() => loadRecords(page - 1)}
              dataTestId="account-settings-admin-log-prev-page-btn"
            >
              {appstr("auditLog.pagination.previous")}
            </Button>
            <Text size="small">
              {appstr("auditLog.pagination.page")} {page + 1} /{" "}
              {Math.max(1, Math.ceil(total / pageSize))}
            </Text>
            <Button
              modifier="secondary"
              disabled={(page + 1) * pageSize >= total || loading}
              onClick={() => loadRecords(page + 1)}
              dataTestId="account-settings-admin-log-next-page-btn"
            >
              {appstr("auditLog.pagination.next")}
            </Button>
          </Flex>
        </Flex>
      </SectionCard>
    </SectionCardGroup>
  );
};

// Round-5 (2026-06-12): switched from `eventTypes`-array contract to scalar
// `eventType` / `source` / `outcome` strings. The array contract round-tripped
// fine in theory (QRFC.send JSON-stringifies the body) but the dropdown
// filters were not actually narrowing the result set in production. Scalar
// strings are unambiguous to debug from the browser network tab and match
// the single-select UI shape directly. Backend accepts both for now.
const filtersToRequest = filters => {
  const out = {};
  if (filters.startDate) out.startDate = filters.startDate;
  if (filters.endDate) out.endDate = filters.endDate;
  if (filters.eventType) out.eventType = filters.eventType;
  if (filters.source) out.source = filters.source;
  if (filters.outcome) out.outcome = filters.outcome;
  if (filters.actorId) out.actorId = filters.actorId;
  if (filters.targetId) out.targetId = filters.targetId;
  return out;
};

// Storybook <Table> column shape: { field, headerName, columnWidth, type, ... }
// `columnWidth` must be one of the values declared in storybook's
// TABLE_PROPS.columnWidths namespace: "5" | "10" | "20" | "25" | "50" | "75"
// (numeric/percentage buckets — anything else resolves to undefined and
// breaks downstream lookups).
// `filterDisabled: true` because the page has its own server-driven filter
// bar above the table; we do not want storybook's column-header filter UI.
const buildAuditColumns = () => [
  {
    field: "timestamp",
    headerName: appstr("auditLog.column.timestamp"),
    columnWidth: "10",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "eventType",
    headerName: appstr("auditLog.column.eventType"),
    columnWidth: "10",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "outcome",
    headerName: appstr("auditLog.column.outcome"),
    columnWidth: "5",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "source",
    headerName: appstr("auditLog.column.source"),
    columnWidth: "10",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "actor",
    headerName: appstr("auditLog.column.actor"),
    columnWidth: "20",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "target",
    headerName: appstr("auditLog.column.target"),
    columnWidth: "20",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "diffKeys",
    headerName: appstr("auditLog.column.diffKeys"),
    columnWidth: "10",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 1 }
  },
  {
    field: "message",
    headerName: appstr("auditLog.column.message"),
    columnWidth: "10",
    type: "overflowText",
    filterDisabled: true,
    overflowTextProps: { clamp: 2 }
  }
];
